import React, { useState } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DynamicTable = ({
  columns,
  data,
  selectedRow,
  onRowSelect,
  onDoubleClick,
  rowKey = "id",
  userIdKey = "userId",
  noDataMessage = "No data available",
  highlightColor = "bg-blue-100",
  hoverColor = "hover:bg-gray-50",
  userRole,
}) => {
  const [clickTimeout, setClickTimeout] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const userId = useSelector((state) => state.auth.id);

  // Modified filtering logic for Store Officer role
  const displayData = userRole?.toLowerCase() === "admin" 
    ? data 
    : userRole?.toLowerCase() === "store officer" 
      ? data.filter(row => row.SOID === userId)
      : data;

  // Calculate pagination
  const totalPages = Math.ceil(displayData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = displayData.slice(startIndex, startIndex + rowsPerPage);

  const handleRowClick = (row) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      if (onDoubleClick) {
        onDoubleClick(row);
      }
    } else {
      const timeoutId = setTimeout(() => {
        if (onRowSelect) {
          onRowSelect(row);
        }
        setClickTimeout(null);
      }, 200);
      setClickTimeout(timeoutId);
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  // Reset page when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // Function to get row background color
  const getRowBackgroundColor = (row) => {
    // Check if the row's Status is "Closed" or any other status that indicates closure
    if (row.Status?.toLowerCase() === "Closed" || row.Status?.toLowerCase() === "Force Closed") {
      return "bg-green-100";
    }
    
    // Check if all approval fields are "Approved"
    if (
      row.L1 === "Approved" &&
      row.L2 === "Approved" &&
      row.PO === "Approved" &&
      row.PH === "Approved" &&
      row.IR === "Approved"
    ) {
      return "bg-green-100";
    }
    
    return selectedRow?.[rowKey] === row[rowKey] ? highlightColor : "";
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100 text-gray-800">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="border border-gray-300 px-6 py-3 text-left text-lg font-semibold"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row) => (
                <tr
                  key={row[rowKey]}
                  className={clsx(
                    "cursor-pointer",
                    getRowBackgroundColor(row),
                    hoverColor,
                    "border-b border-gray-300"
                  )}
                  onClick={() => handleRowClick(row)}
                >
                  {columns.map((column) => (
                    <td
                      key={`${row[rowKey]}-${column.key}`}
                      className="border border-gray-300 px-5 py-3 text-base"
                    >
                      {row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border px-4 py-2 text-center text-gray-500"
                >
                  {noDataMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {displayData.length > 0 && (
        <div className="flex items-center justify-between px-4">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, displayData.length)} of{" "}
            {displayData.length} entries
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={clsx(
                "inline-flex items-center rounded px-3 py-2",
                "border border-gray-300 bg-white text-sm font-medium",
                "focus:outline-none focus:ring-2 focus:ring-blue-500",
                currentPage === 1
                  ? "cursor-not-allowed text-gray-400"
                  : "text-gray-700 hover:bg-gray-50"
              )}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="ml-1">Previous</span>
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={clsx(
                "inline-flex items-center rounded px-3 py-2",
                "border border-gray-300 bg-white text-sm font-medium",
                "focus:outline-none focus:ring-2 focus:ring-blue-500",
                currentPage === totalPages
                  ? "cursor-not-allowed text-gray-400"
                  : "text-gray-700 hover:bg-gray-50"
              )}
            >
              <span className="mr-1">Next</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicTable;